import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  LayoutDashboard,
  Scissors,
  Image,
  Star,
  CalendarDays,
  MessageSquare,
  LogOut,
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  Upload,
  Loader2,
  Bell,
  CheckCircle,
  Sparkles,
  Megaphone,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Send,
  Phone,
  Users,
  Mail,
  CalendarPlus,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { API, SITE_URL, authHeaders, resolveUrl, ImageUploadField } from "./shared";

const adTemplates = {
  meta: [
    { title: "10% Off First Visit", body: "Book online at Afro Magic Braiding Hair and get 10% OFF your first visit! Knotless braids, box braids, cornrows, twists & more. Expert African hair braiding in Southfield, MI. Book now!", cta: "Book Now" },
    { title: "New Styles Available", body: "Looking for a fresh look? Afro Magic Braiding Hair offers 22+ braiding styles starting at $50. Knotless braids, Boho braids, Fulani braids, faux locs & more. Walk-ins welcome! 22560 Telegraph Rd, Southfield.", cta: "See Our Styles" },
    { title: "Summer Ready Braids", body: "Get your hair summer-ready at Afro Magic! Professional braiding from $50. Knotless braids $200+, Box braids $200+, Cornrows $50+. Book online & save 10%! Serving Southfield, Detroit & Windsor.", cta: "Book & Save" },
  ],
  google: [
    { headline: "African Hair Braiding Near Me - 10% Off Online Bookings", description: "Professional knotless braids, box braids, cornrows & twists in Southfield MI. 22+ styles from $50. Book online & get 10% off. Walk-ins welcome. Open 24/7 by appointment." },
    { headline: "Knotless Braids $200+ | Afro Magic Braiding", description: "Expert African hair braiding at 22560 Telegraph Rd, Southfield. Box braids, Fulani braids, faux locs, men's styles & more. 5-star rated. Book now!" },
  ],
  tiktok: [
    { hook: "POV: You just got the PERFECT braids", body: "Afro Magic Braiding Hair in Southfield, MI. 22+ styles, prices starting at $50. Book online and get 10% off! Link in bio.", hashtags: "#braids #knotlessbraids #boxbraids #africanbraiding #southfieldmi #hairbraiding #protectivestyles" },
    { hook: "Stop scrolling if you need braids near Detroit", body: "Knotless braids, box braids, cornrows, twists & more at Afro Magic. Walk-ins welcome or book online for 10% off! 22560 Telegraph Rd, Southfield.", hashtags: "#braids #hairtok #knotlessbraids #detroitbraids #africanbraiding #protectivestyles" },
  ],
};

const targetingGuide = {
  location: "22560 Telegraph Rd, Southfield, MI 48033",
  radius: "20 miles",
  audience: "Women 18-55, interested in: Hair care, Braids, African hair braiding, Protective hairstyles, Natural hair, Beauty salons",
  keywords: "african hair braiding near me, knotless braids near me, box braids southfield, braiding salon detroit, cornrows near me, hair braiding michigan",
};

const MarketingTab = () => {
  const [copiedId, setCopiedId] = useState(null);

  const copyText = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const utmLink = (platform) => `${SITE_URL}?utm_source=${platform}&utm_medium=paid&utm_campaign=braiding`;

  return (
    <div data-testid="admin-marketing-tab">
      <h2 className="font-serif text-2xl text-brand-text mb-2">Marketing Hub</h2>
      <p className="font-sans text-sm text-brand-text-muted mb-8">Ready-to-use ad templates and targeting for Meta, Google & TikTok. Copy, paste, and launch!</p>

      {/* Targeting Setup */}
      <div className="border border-brand-gold/30 bg-brand-gold/5 p-5 mb-8" data-testid="targeting-guide">
        <h3 className="font-serif text-lg text-brand-gold mb-3">Targeting Setup (Use on All Platforms)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="font-sans text-xs text-brand-text-muted mb-1">Location Targeting</p>
            <p className="font-sans text-sm text-brand-text">{targetingGuide.location}</p>
            <p className="font-sans text-xs text-brand-gold mt-1">Radius: {targetingGuide.radius}</p>
          </div>
          <div>
            <p className="font-sans text-xs text-brand-text-muted mb-1">Target Audience</p>
            <p className="font-sans text-xs text-brand-text leading-relaxed">{targetingGuide.audience}</p>
          </div>
          <div className="md:col-span-2">
            <p className="font-sans text-xs text-brand-text-muted mb-1">Google Keywords</p>
            <div className="flex items-center gap-2">
              <p className="font-sans text-xs text-brand-text flex-1">{targetingGuide.keywords}</p>
              <button onClick={() => copyText(targetingGuide.keywords, "keywords")} className="p-1.5 border border-brand-border text-brand-text-muted hover:text-brand-gold transition-colors flex-shrink-0">
                {copiedId === "keywords" ? <CheckCircle size={12} className="text-green-400" /> : <Copy size={12} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* UTM Tracking Links */}
      <div className="border border-brand-border bg-brand-surface p-5 mb-8" data-testid="utm-links">
        <h3 className="font-serif text-lg text-brand-text mb-3">Tracking Links (Use These in Your Ads)</h3>
        <p className="font-sans text-xs text-brand-text-muted mb-3">These links track which platform brings clients. Use them as your ad destination URL.</p>
        {["meta", "google", "tiktok"].map((p) => (
          <div key={p} className="flex items-center gap-3 mb-2">
            <span className="font-sans text-xs text-brand-gold uppercase w-16">{p}</span>
            <code className="font-mono text-xs text-brand-text-muted flex-1 truncate bg-brand-bg px-2 py-1 border border-brand-border">{utmLink(p)}</code>
            <button onClick={() => copyText(utmLink(p), `utm-${p}`)} className="p-1.5 border border-brand-border text-brand-text-muted hover:text-brand-gold transition-colors flex-shrink-0">
              {copiedId === `utm-${p}` ? <CheckCircle size={12} className="text-green-400" /> : <Copy size={12} />}
            </button>
          </div>
        ))}
      </div>

      {/* META ADS */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg text-brand-text flex items-center gap-2">
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-blue-500"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            Meta (Facebook & Instagram)
          </h3>
          <a href="https://business.facebook.com/latest/ad_center" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 font-sans text-xs text-brand-gold hover:text-brand-gold-light" data-testid="meta-ads-link">
            Open Meta Ads Manager <ExternalLink size={12} />
          </a>
        </div>
        <div className="space-y-3">
          {adTemplates.meta.map((ad, i) => (
            <div key={ad.title} className="p-4 border border-brand-border bg-brand-surface" data-testid={`meta-ad-${i}`}>
              <div className="flex items-start justify-between mb-2">
                <p className="font-sans text-sm font-semibold text-brand-text">{ad.title}</p>
                <button onClick={() => copyText(`${ad.title}\n\n${ad.body}\n\n${utmLink("meta")}`, `meta-${i}`)} className="flex items-center gap-1 px-2 py-1 border border-brand-gold text-brand-gold font-sans text-xs hover:bg-brand-gold hover:text-black transition-colors">
                  {copiedId === `meta-${i}` ? <><CheckCircle size={10} /> Copied</> : <><Copy size={10} /> Copy Ad</>}
                </button>
              </div>
              <p className="font-sans text-xs text-brand-text-muted leading-relaxed">{ad.body}</p>
              <p className="font-sans text-[10px] text-brand-gold mt-2">CTA Button: {ad.cta}</p>
            </div>
          ))}
        </div>
      </div>

      {/* GOOGLE ADS */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg text-brand-text flex items-center gap-2">
            <svg viewBox="0 0 24 24" className="w-5 h-5"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Google Ads
          </h3>
          <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 font-sans text-xs text-brand-gold hover:text-brand-gold-light" data-testid="google-ads-link">
            Open Google Ads <ExternalLink size={12} />
          </a>
        </div>
        <div className="space-y-3">
          {adTemplates.google.map((ad, i) => (
            <div key={ad.headline} className="p-4 border border-brand-border bg-brand-surface" data-testid={`google-ad-${i}`}>
              <div className="flex items-start justify-between mb-2">
                <p className="font-sans text-sm font-semibold text-brand-text">{ad.headline}</p>
                <button onClick={() => copyText(`Headline: ${ad.headline}\nDescription: ${ad.description}\nURL: ${utmLink("google")}`, `google-${i}`)} className="flex items-center gap-1 px-2 py-1 border border-brand-gold text-brand-gold font-sans text-xs hover:bg-brand-gold hover:text-black transition-colors">
                  {copiedId === `google-${i}` ? <><CheckCircle size={10} /> Copied</> : <><Copy size={10} /> Copy Ad</>}
                </button>
              </div>
              <p className="font-sans text-xs text-brand-text-muted leading-relaxed">{ad.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* TIKTOK ADS */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg text-brand-text flex items-center gap-2">
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-brand-text"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
            TikTok Ads
          </h3>
          <a href="https://ads.tiktok.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 font-sans text-xs text-brand-gold hover:text-brand-gold-light" data-testid="tiktok-ads-link">
            Open TikTok Ads <ExternalLink size={12} />
          </a>
        </div>
        <div className="space-y-3">
          {adTemplates.tiktok.map((ad, i) => (
            <div key={ad.hook} className="p-4 border border-brand-border bg-brand-surface" data-testid={`tiktok-ad-${i}`}>
              <div className="flex items-start justify-between mb-2">
                <p className="font-sans text-sm font-semibold text-brand-text">"{ad.hook}"</p>
                <button onClick={() => copyText(`${ad.hook}\n\n${ad.body}\n\n${ad.hashtags}\n\n${utmLink("tiktok")}`, `tiktok-${i}`)} className="flex items-center gap-1 px-2 py-1 border border-brand-gold text-brand-gold font-sans text-xs hover:bg-brand-gold hover:text-black transition-colors">
                  {copiedId === `tiktok-${i}` ? <><CheckCircle size={10} /> Copied</> : <><Copy size={10} /> Copy Ad</>}
                </button>
              </div>
              <p className="font-sans text-xs text-brand-text-muted leading-relaxed mb-2">{ad.body}</p>
              <p className="font-sans text-[10px] text-blue-400">{ad.hashtags}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Start Guide */}
      <div className="border border-brand-border bg-brand-surface p-5" data-testid="quick-start-guide">
        <h3 className="font-serif text-lg text-brand-text mb-4">Quick Start Guide</h3>
        <div className="space-y-4">
          {[
            { step: "1", platform: "Meta (Best for Hair Braiding)", url: "https://business.facebook.com", instructions: "Create Business Manager account. Go to Ad Center. Click 'Create Ad'. Choose 'Get More Website Visitors'. Paste the ad copy above. Set location: 22560 Telegraph Rd, Southfield MI, 20 mile radius. Budget: Start with $5-10/day. Target: Women 18-55, interests in braids/hair care." },
            { step: "2", platform: "Google Ads", url: "https://ads.google.com", instructions: "Create Google Ads account. Choose 'Search Campaign'. Paste the headlines and descriptions above. Add keywords from the targeting section. Set location: Southfield MI, 20 mile radius. Budget: Start with $10/day." },
            { step: "3", platform: "TikTok Ads", url: "https://ads.tiktok.com", instructions: "Create TikTok Business account. Choose 'Traffic' objective. Record a short video showing your braiding work. Use the captions/hooks above. Set location: Michigan, 20 miles from Southfield. Budget: Start with $5/day. Target: Women 18-45." },
          ].map((item) => (
            <div key={item.step} className="flex gap-4">
              <span className="font-serif text-xl text-brand-gold font-semibold w-8 flex-shrink-0">{item.step}</span>
              <div>
                <a href={item.url} target="_blank" rel="noopener noreferrer" className="font-sans text-sm font-semibold text-brand-text hover:text-brand-gold flex items-center gap-1">
                  {item.platform} <ExternalLink size={12} />
                </a>
                <p className="font-sans text-xs text-brand-text-muted leading-relaxed mt-1">{item.instructions}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MarketingTab;
